import React, { useContext, useEffect, useState } from 'react';
import { Dimensions, SafeAreaView, ScrollView, View } from 'react-native';
import moment from 'moment';
import { Text } from '@/components/Text';
import { useStoreActions, useStoreState } from '@/stores/store';
import { CustomCheckBox } from '@/components/SharedComponents/CustomCheckBox/';
import { fetchUserConsents, updateUserEpa } from '@/api/services/user';
import { PressableNeolexon } from '@/components/PressableNeolexon';
import { navigate } from '@/navigations/RootNavigation';
import { appTheme } from '@/config/colors';
import { connectWithEpa, writeToEpa } from '@/utils/epaAuth';
import { EpaAuthContext } from '@/contexts/EpaAuthContext';

import { styles } from './styles';

export const EpaScreen = () => {
  const { storedUser, userConsents } = useStoreState((state) => ({
    storedUser: state.signedInUser,
    userConsents: state.userConsents,
  }));
  const { setUserInsuranceId, toggleUserAutoUpload } = useStoreActions(
    (actions) => ({
      setUserInsuranceId: actions.setUserInsuranceId,
      toggleUserAutoUpload: actions.setUserIsAutoUploadEpa,
    }),
  );

  let [isConnectEnabled, setConnectState] = useState<boolean>(false);
  const [windowDimensions, setWindowDimensions] = useState<{
    width: number;
    height: number;
  }>(Dimensions.get('window'));
  let [showEpaSuccess, setShowEpaSuccess] = useState<boolean>(false);
  let [showEpaError, setShowEpaError] = useState<boolean>(false);
  let [showEpaConnectError, setShowEpaConnectError] = useState<boolean>(false);

  const authContext = useContext(EpaAuthContext);
  const epaLastUpdatedAt = moment(
    storedUser.user?.userEpa?.epaLastUpdatedAt,
  ).format('DD-MM-YYYY HH:mm');
  const consentText =
    'Ich willige ein, dass Übungsdauer und Übungsergebnisse von neolexon in meiner persönlichen elektronischen Patientenakte gespeichert werden. Ich kann meine Einwilligung jederzeit widerrufen. Die Einwilligung ist optional.';

  useEffect(() => {
    const dimensionChange = Dimensions.addEventListener(
      'change',
      ({ window }) => {
        setWindowDimensions(window);
      },
    );
    return () => dimensionChange?.remove();
  }, []);

  useEffect(() => {
    (async () => {
      await fetchUserConsents();
    })();
  }, []);

  let canWriteToEpa = false;
  for (let consent of userConsents) {
    if (consent.name === 'WriteToEpa') {
      canWriteToEpa = consent.givenConsent;
    }
  }

  return (
    <SafeAreaView style={styles.fullHeight}>
      <ScrollView>
        <View
          style={[
            styles.container,
            { width: windowDimensions.width > 1024 ? '70%' : '100%' },
          ]}
        >
          <Text style={[styles.text, styles.textMargin, styles.textCenter]}>
            Alle gesetzlich Versicherten können eine elektronische Patientenakte
            (ePA) von ihrer Krankenkasse erhalten, in der medizinische Befunde
            und weitere persönliche Gesundheitsdaten gespeichert werden. Sie
            können selbst bestimmen, ob und in welchem Umfang Sie die ePA nutzen
            möchten.
          </Text>
          <Text style={[styles.text, styles.textMargin, styles.textCenter]}>
            Sie haben hierbei auch die Möglichkeit die Übungsdauer und
            Übungsergebnisse aus neolexon in ihre ePA zu übertragen. Bei der
            automatischen Übertragung werden vergangene Übungsdaten jeden Tag um
            22:00 Uhr in die ePA übertragen.
          </Text>
          {storedUser.user?.idpInsuranceId && !canWriteToEpa ? (
            <Text
              style={[
                styles.text,
                styles.textCenter,
                styles.textMargin,
                styles.textRed,
              ]}
            >
              Es liegt keine Einwilligung für die Speicherung der Übungen in
              ihre ePA vor. Sie können ihre Einwilligungen{' '}
              <PressableNeolexon
                onPress={() => {
                  navigate('Einwilligungen');
                }}
                style={styles.linkToConsents}
              >
                <Text>hier</Text>
              </PressableNeolexon>{' '}
              verwalten.
            </Text>
          ) : null}
          {storedUser.user?.idpInsuranceId && canWriteToEpa ? (
            <View style={[styles.textMargin, styles.itemsCenter]}>
              <Text style={styles.statusText}>
                Status: GesundheitsID verbunden
              </Text>
              <View style={styles.statusText}>
                <CustomCheckBox
                  isLabelComponent={false}
                  checked={storedUser.user?.userEpa?.autoUpload ?? false}
                  onChecked={() => {
                    try {
                      toggleUserAutoUpload(
                        !storedUser.user?.userEpa?.autoUpload,
                      );
                      updateUserEpa({
                        autoUpload: !storedUser.user?.userEpa?.autoUpload,
                      });
                    } catch {}
                  }}
                  disabled={!canWriteToEpa}
                  checkedColor={appTheme.primary}
                  label={'Ergebnisse automatisch in die ePA schreiben'}
                />
              </View>
              <PressableNeolexon
                style={styles.buttonStyle}
                onPress={async () => {
                  let writeToEpaSuccess = false;
                  try {
                    await writeToEpa();
                    writeToEpaSuccess = true;
                  } catch {}
                  setShowEpaError(!writeToEpaSuccess);
                  setShowEpaSuccess(writeToEpaSuccess);
                }}
                disabled={!canWriteToEpa}
              >
                <Text style={styles.buttonTextStyle}>
                  {'Ergebnisse in meine ePA übertragen'}
                </Text>
              </PressableNeolexon>
              <Text>Letztes ePA Update: {epaLastUpdatedAt}</Text>

              {showEpaSuccess ? (
                <Text
                  style={[
                    styles.textSmall,
                    styles.textCenter,
                    styles.textGreen,
                  ]}
                >
                  Übungsergebnisse erfolgreich in die ePA geschrieben
                </Text>
              ) : null}

              {showEpaError ? (
                <Text
                  style={[styles.textSmall, styles.textCenter, styles.textRed]}
                >
                  Es ist ein Fehler beim Schreiben in die ePA aufgetreten. Bitte
                  probieren Sie es später erneut.
                </Text>
              ) : null}
              <Text
                style={[styles.textSmall, styles.textCenter, styles.textMargin]}
              >
                Hinweis: Sie können sich nun auch mit Ihrer GesundheitsID in der
                neolexon App einloggen.
              </Text>
            </View>
          ) : null}
          {!storedUser.user?.idpInsuranceId && (
            <View>
              <View style={styles.consentContainer}>
                <CustomCheckBox
                  checked={isConnectEnabled}
                  onChecked={(value: boolean) => {
                    setConnectState(!value);
                  }}
                />

                <Text style={[styles.text, styles.textMargin]}>
                  {consentText}
                </Text>
              </View>
              <PressableNeolexon
                style={styles.buttonStyle}
                onPress={async () => {
                  await connectWithEpa(
                    authContext,
                    (insuranceId) => {
                      setUserInsuranceId(insuranceId);
                      fetchUserConsents();
                    },
                    () => {
                      setShowEpaConnectError(true);
                    },
                  );
                }}
                disabled={!isConnectEnabled}
              >
                <Text style={styles.buttonTextStyle}>{'Verbinden'}</Text>
              </PressableNeolexon>
              {showEpaConnectError ? (
                <Text
                  style={[styles.textSmall, styles.textCenter, styles.textRed]}
                >
                  Es ist ein Fehler beim Verbinden mit der ePA aufgetreten.
                  Falls dieses Problem besteht, melden Sie sich bitte bei
                  unserem Support.
                </Text>
              ) : null}
              <Text
                style={[styles.textSmall, styles.textCenter, styles.textMargin]}
              >
                Hinweis: hierzu müssen Sie sich einmalig mit Ihrer GesundheitsID
                anmelden, um sich mit der ePA zu verbinden
              </Text>
            </View>
          )}
        </View>
      </ScrollView>
    </SafeAreaView>
  );
};
